import {
  collection,
  addDoc,
  query,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Booking, Route } from '../types';

export interface EmailTemplate {
  subject: string;
  htmlBody: string;
  textBody?: string;
}

export interface EmailData {
  to: string;
  toName?: string;
  template: string;
  data: Record<string, any>;
  priority: 'low' | 'normal' | 'high';
  scheduledAt?: Date;
}

export interface EmailLog {
  id: string;
  to: string;
  subject: string;
  status: 'pending' | 'sent' | 'failed' | 'delivered';
  template: string;
  data: Record<string, any>;
  sentAt?: Date;
  error?: string;
  createdAt: Date;
}

export class EmailService {
  private static templates: Record<string, EmailTemplate> = {
    // Booking Confirmation
    bookingConfirmation: {
      subject: '🎫 Booking Confirmed - Wote InterCity',
      htmlBody: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Booking Confirmation</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f4f4f4; }
            .container { background-color: #fff; padding: 30px; border-radius: 10px; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { width: 80px; height: 80px; margin-bottom: 20px; }
            .title { color: #2c5aa0; font-size: 28px; margin-bottom: 10px; }
            .subtitle { color: #666; font-size: 16px; }
            .content { margin: 30px 0; }
            .booking-details { background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; margin: 10px 0; padding: 8px 0; border-bottom: 1px solid #eee; }
            .detail-label { font-weight: bold; color: #555; }
            .detail-value { color: #333; }
            .qr-section { text-align: center; margin: 30px 0; }
            .qr-code { width: 200px; height: 200px; border: 2px solid #ddd; border-radius: 8px; }
            .button { display: inline-block; background-color: #2c5aa0; color: #fff; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; margin: 20px 0; }
            .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 14px; }
            .success { color: #27ae60; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo">🚌</div>
              <h1 class="title">Booking Confirmed!</h1>
              <p class="subtitle">Your journey is ready</p>
            </div>
            <div class="content">
              <p class="success">✅ Your booking has been confirmed successfully!</p>
              <div class="booking-details">
                <h3>Booking Details</h3>
                <div class="detail-row">
                  <span class="detail-label">Booking Reference:</span>
                  <span class="detail-value">%BOOKING_REFERENCE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Passenger Name:</span>
                  <span class="detail-value">%PASSENGER_NAME%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Route:</span>
                  <span class="detail-value">%ROUTE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Travel Date:</span>
                  <span class="detail-value">%TRAVEL_DATE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Departure Time:</span>
                  <span class="detail-value">%DEPARTURE_TIME%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Seat Number:</span>
                  <span class="detail-value">%SEAT_NUMBER%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Total Amount:</span>
                  <span class="detail-value">%TOTAL_AMOUNT%</span>
                </div>
              </div>
              <div class="qr-section">
                <h3>Your QR Code</h3>
                <p>Show this QR code to the conductor when boarding:</p>
                <img src="%QR_CODE_URL%" alt="Booking QR Code" class="qr-code">
              </div>
              <p><strong>Important:</strong> Please arrive at the bus station 15 minutes before departure time.</p>
            </div>
            <div class="footer">
              <p>© 2024 Wote InterCity. All rights reserved.</p>
              <p>For support, contact us at support@woteintercity.com</p>
            </div>
          </div>
        </body>
        </html>
      `
    },

    // Travel Reminder
    travelReminder: {
      subject: '🚌 Travel Reminder - Your Journey Tomorrow',
      htmlBody: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Travel Reminder</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f4f4f4; }
            .container { background-color: #fff; padding: 30px; border-radius: 10px; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { width: 80px; height: 80px; margin-bottom: 20px; }
            .title { color: #2c5aa0; font-size: 28px; margin-bottom: 10px; }
            .subtitle { color: #666; font-size: 16px; }
            .content { margin: 30px 0; }
            .reminder-box { background-color: #fff3cd; border: 1px solid #ffeaa7; color: #856404; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .booking-details { background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; margin: 10px 0; padding: 8px 0; border-bottom: 1px solid #eee; }
            .detail-label { font-weight: bold; color: #555; }
            .detail-value { color: #333; }
            .button { display: inline-block; background-color: #2c5aa0; color: #fff; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; margin: 20px 0; }
            .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo">⏰</div>
              <h1 class="title">Travel Reminder</h1>
              <p class="subtitle">Your journey is tomorrow!</p>
            </div>
            <div class="content">
              <div class="reminder-box">
                <h3>⏰ Reminder: Your journey is tomorrow!</h3>
                <p>Don't forget your scheduled trip with Wote InterCity. Please arrive at the bus station 15 minutes before departure time.</p>
              </div>
              <div class="booking-details">
                <h3>Journey Details</h3>
                <div class="detail-row">
                  <span class="detail-label">Route:</span>
                  <span class="detail-value">%ROUTE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Departure Time:</span>
                  <span class="detail-value">%DEPARTURE_TIME%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Seat Number:</span>
                  <span class="detail-value">%SEAT_NUMBER%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Booking Reference:</span>
                  <span class="detail-value">%BOOKING_REFERENCE%</span>
                </div>
              </div>
              <p><strong>What to bring:</strong></p>
              <ul>
                <li>Valid ID or passport</li>
                <li>Your booking confirmation (QR code)</li>
                <li>Any luggage (within weight limits)</li>
              </ul>
            </div>
            <div class="footer">
              <p>© 2024 Wote InterCity. All rights reserved.</p>
              <p>For support, contact us at support@woteintercity.com</p>
            </div>
          </div>
        </body>
        </html>
      `
    },

    // Feedback Request
    feedbackRequest: {
      subject: '💬 How was your journey? - Share your feedback',
      htmlBody: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Feedback Request</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f4f4f4; }
            .container { background-color: #fff; padding: 30px; border-radius: 10px; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { width: 80px; height: 80px; margin-bottom: 20px; }
            .title { color: #2c5aa0; font-size: 28px; margin-bottom: 10px; }
            .subtitle { color: #666; font-size: 16px; }
            .content { margin: 30px 0; }
            .journey-details { background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; margin: 10px 0; padding: 8px 0; border-bottom: 1px solid #eee; }
            .detail-label { font-weight: bold; color: #555; }
            .detail-value { color: #333; }
            .button { display: inline-block; background-color: #27ae60; color: #fff; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; margin: 20px 0; }
            .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo">💬</div>
              <h1 class="title">How was your journey?</h1>
              <p class="subtitle">Your feedback helps us improve</p>
            </div>
            <div class="content">
              <p>We hope you had a pleasant journey with Wote InterCity! Your feedback is important to us and helps us improve our service.</p>
              <div class="journey-details">
                <h3>Your Journey</h3>
                <div class="detail-row">
                  <span class="detail-label">Route:</span>
                  <span class="detail-value">%ROUTE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Travel Date:</span>
                  <span class="detail-value">%TRAVEL_DATE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Seat Number:</span>
                  <span class="detail-value">%SEAT_NUMBER%</span>
                </div>
              </div>
              <p>Please take a moment to rate your experience and share any feedback:</p>
              <a href="%FEEDBACK_URL%" class="button">Share Your Feedback</a>
              <p>Your feedback helps us:</p>
              <ul>
                <li>Improve our service quality</li>
                <li>Enhance passenger experience</li>
                <li>Maintain high standards</li>
              </ul>
            </div>
            <div class="footer">
              <p>© 2024 Wote InterCity. All rights reserved.</p>
              <p>For support, contact us at support@woteintercity.com</p>
            </div>
          </div>
        </body>
        </html>
      `
    },

    // Admin Notification
    adminNotification: {
      subject: '🔔 New Booking Alert - Wote InterCity Admin',
      htmlBody: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Admin Notification</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f4f4f4; }
            .container { background-color: #fff; padding: 30px; border-radius: 10px; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { width: 80px; height: 80px; margin-bottom: 20px; }
            .title { color: #2c5aa0; font-size: 28px; margin-bottom: 10px; }
            .subtitle { color: #666; font-size: 16px; }
            .content { margin: 30px 0; }
            .alert-box { background-color: #d4edda; border: 1px solid #c3e6cb; color: #155724; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .booking-details { background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; margin: 10px 0; padding: 8px 0; border-bottom: 1px solid #eee; }
            .detail-label { font-weight: bold; color: #555; }
            .detail-value { color: #333; }
            .button { display: inline-block; background-color: #2c5aa0; color: #fff; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; margin: 20px 0; }
            .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo">🔔</div>
              <h1 class="title">New Booking Alert</h1>
              <p class="subtitle">Wote InterCity Admin Dashboard</p>
            </div>
            <div class="content">
              <div class="alert-box">
                <h3>🔔 New Booking Received!</h3>
                <p>A new booking has been made on the Wote InterCity platform.</p>
              </div>
              <div class="booking-details">
                <h3>Booking Details</h3>
                <div class="detail-row">
                  <span class="detail-label">Booking Reference:</span>
                  <span class="detail-value">%BOOKING_REFERENCE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Passenger Name:</span>
                  <span class="detail-value">%PASSENGER_NAME%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Route:</span>
                  <span class="detail-value">%ROUTE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Travel Date:</span>
                  <span class="detail-value">%TRAVEL_DATE%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Seat Number:</span>
                  <span class="detail-value">%SEAT_NUMBER%</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Total Amount:</span>
                  <span class="detail-value">%TOTAL_AMOUNT%</span>
                </div>
              </div>
              <a href="%ADMIN_URL%" class="button">View in Admin Dashboard</a>
            </div>
            <div class="footer">
              <p>© 2024 Wote InterCity. All rights reserved.</p>
              <p>Admin Dashboard: https://wote-intercity.web.app</p>
            </div>
          </div>
        </body>
        </html>
      `
    }
  };

  // Send email
  static async sendEmail(emailData: EmailData): Promise<{ success: boolean; error?: string }> {
    try {
      // Log email to Firestore
      const emailLog: Omit<EmailLog, 'id'> = {
        to: emailData.to,
        subject: this.templates[emailData.template]?.subject || 'Wote InterCity Notification',
        status: 'pending',
        template: emailData.template,
        data: emailData.data,
        createdAt: new Date()
      };

      await addDoc(collection(db, 'emailQueue'), emailLog);

      // In a real implementation, you would integrate with an email service like:
      // - SendGrid
      // - Mailgun
      // - AWS SES
      // - Nodemailer with SMTP

      // For now, we'll simulate email sending
      console.log(`📧 Email queued: ${emailData.template} to ${emailData.to}`);

      return { success: true };
    } catch (error: any) {
      console.error('Email sending error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send booking confirmation email
  static async sendBookingConfirmation(booking: Booking, route: Route, qrCodeUrl?: string): Promise<{ success: boolean; error?: string }> {
    const template = this.templates.bookingConfirmation;
    // Process template variables (for future use in actual email sending)
    template.htmlBody
      .replace('%BOOKING_REFERENCE%', booking.bookingReference)
      .replace('%PASSENGER_NAME%', booking.passengerName)
      .replace('%ROUTE%', `${route.fromCity} → ${route.toCity}`)
      .replace('%TRAVEL_DATE%', booking.travelDate.toLocaleDateString())
      .replace('%DEPARTURE_TIME%', route.departureTime)
      .replace('%SEAT_NUMBER%', booking.seatNumber)
      .replace('%TOTAL_AMOUNT%', `₵${booking.totalAmount.toFixed(2)}`)
      .replace('%QR_CODE_URL%', qrCodeUrl || 'https://via.placeholder.com/200x200?text=QR+Code');

    return this.sendEmail({
      to: booking.passengerEmail,
      toName: booking.passengerName,
      template: 'bookingConfirmation',
      data: {
        bookingReference: booking.bookingReference,
        passengerName: booking.passengerName,
        route: `${route.fromCity} → ${route.toCity}`,
        travelDate: booking.travelDate.toLocaleDateString(),
        departureTime: route.departureTime,
        seatNumber: booking.seatNumber,
        totalAmount: `₵${booking.totalAmount.toFixed(2)}`,
        qrCodeUrl: qrCodeUrl
      },
      priority: 'high'
    });
  }

  // Send travel reminder email
  static async sendTravelReminder(booking: Booking, route: Route): Promise<{ success: boolean; error?: string }> {
    return this.sendEmail({
      to: booking.passengerEmail,
      toName: booking.passengerName,
      template: 'travelReminder',
      data: {
        route: `${route.fromCity} → ${route.toCity}`,
        departureTime: route.departureTime,
        seatNumber: booking.seatNumber,
        bookingReference: booking.bookingReference
      },
      priority: 'normal'
    });
  }

  // Send feedback request email
  static async sendFeedbackRequest(booking: Booking, route: Route): Promise<{ success: boolean; error?: string }> {
    return this.sendEmail({
      to: booking.passengerEmail,
      toName: booking.passengerName,
      template: 'feedbackRequest',
      data: {
        route: `${route.fromCity} → ${route.toCity}`,
        travelDate: booking.travelDate.toLocaleDateString(),
        seatNumber: booking.seatNumber,
        feedbackUrl: `https://wote-intercity.web.app/feedback?booking=${booking.id}`
      },
      priority: 'low'
    });
  }

  // Send admin notification email
  static async sendAdminNotification(booking: Booking, route: Route): Promise<{ success: boolean; error?: string }> {
    return this.sendEmail({
      to: 'admin@woteintercity.com',
      toName: 'Wote InterCity Admin',
      template: 'adminNotification',
      data: {
        bookingReference: booking.bookingReference,
        passengerName: booking.passengerName,
        route: `${route.fromCity} → ${route.toCity}`,
        travelDate: booking.travelDate.toLocaleDateString(),
        seatNumber: booking.seatNumber,
        totalAmount: `₵${booking.totalAmount.toFixed(2)}`,
        adminUrl: 'https://wote-intercity.web.app/bookings'
      },
      priority: 'high'
    });
  }

  // Get email logs
  static async getEmailLogs(limitCount: number = 50): Promise<EmailLog[]> {
    try {
      const emailLogsRef = collection(db, 'emailQueue');
      const q = query(emailLogsRef, orderBy('createdAt', 'desc'), limit(limitCount));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        sentAt: doc.data().sentAt?.toDate()
      })) as EmailLog[];
    } catch (error) {
      console.error('Error fetching email logs:', error);
      return [];
    }
  }

  // Schedule email
  static async scheduleEmail(emailData: EmailData, scheduledAt: Date): Promise<{ success: boolean; error?: string }> {
    return this.sendEmail({
      ...emailData,
      scheduledAt
    });
  }
}
